import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useUserContext } from '../UserContext';
import classNames from 'classnames';
import { CommentD, SagaLocation, SpaceOperations } from '@saga/shared';
import { Member } from '@/types';
import { stringToColor } from '@/../../shared/src/utils/Colors';
import { usePerformActionWithYBlocks } from '../RealtimeDocumentProvider';
import { CornerDownLeft, Trash } from 'react-feather';
import { PopOver as RadixPopOver, usePopover } from '../popover/PopOver';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { CommentIconSvg } from '../icons/CommentIconSvg';
import { CommenterInitialsIcon } from '../icons/CommenterInitialsIcon';
import { useMembers, getFullMemberName } from '../MembersProvider';
import MemberAvatar from '../MemberAvatar';
import { AVAILABLE_COLORS } from '@/constants';
import * as Popover from '@radix-ui/react-popover';
import { useArrowSelectableIndex } from '../popover/Dropdown';
import Button from '../styled/Button';
import { scrollToSelectedIndex } from '../editor/Suggestions';
import * as api from '@saga/api';
import { useCurrentWorkspace } from '../WorkspaceContext';

interface CommentIconProps {
    blockId: string;
    onCommentClick: () => void;
    comments?: CommentD[];
    isActiveAndPopoverOpen: boolean;
    isParentHovered?: boolean;
}

const CommentIconComponent = ({
    blockId,
    onCommentClick,
    comments,
    isActiveAndPopoverOpen,
    isParentHovered,
}: CommentIconProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const hasComments = comments && comments?.length > 0;
    const { getMember } = useMembers();

    const processedCommenterInitials = useMemo(() => {
        const currentHasComments = comments && comments.length > 0;
        if (!currentHasComments || !comments) {
            return [];
        }
        const uniqueCommenterIds = [...new Set(comments.map((c) => c.userId).filter((id): id is string => !!id))];
        return uniqueCommenterIds.slice(0, 3).map((userId) => {
            const member = getMember(userId);
            let initial = '?';
            if (member?.firstName) {
                initial = member.firstName.charAt(0);
            } else if (member?.lastName) {
                initial = member.lastName.charAt(0);
            }
            return { initial: initial, id: userId };
        });
    }, [comments, getMember]);

    const handleIconClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            onCommentClick();
        },
        [onCommentClick],
    );

    return (
        <div
            className={classNames(
                'pl-1 comment-icon-container relative select-none user-select-none',
                'transition-opacity duration-150',
                {
                    'opacity-75 hover:opacity-100': hasComments,
                    'opacity-100': !hasComments && (isHovered || isActiveAndPopoverOpen || !!isParentHovered),
                    'opacity-0': !hasComments && !isHovered && !isActiveAndPopoverOpen && !isParentHovered,
                },
            )}
            onMouseDown={(e) => e.preventDefault()}
            data-testid={`comment-icon-${blockId}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={classNames('flex items-center justify-start w-8 h-6 rounded-full cursor-pointer')}
                onClick={handleIconClick}
            >
                {hasComments && processedCommenterInitials.length > 0 ? (
                    <CommenterInitialsIcon commenters={processedCommenterInitials} />
                ) : (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full shadow-sm hover:shadow bg-white dark:bg-zinc-700">
                        <CommentIconSvg className={'text-gray-400 hover:text-gray-500'} />
                    </div>
                )}
            </div>
        </div>
    );
};

export const CommentIcon = React.memo(CommentIconComponent);

interface CommentsPopOverProps {
    comments?: CommentD[];
    blockId: string;
    entityId: string;
    onClose: () => void;
    setShowCommentCard: (show: boolean) => void;
    showCommentCard: boolean;
    attachToRef: React.RefObject<HTMLDivElement>;
}

export default function CommentsPopOver({
    comments,
    blockId,
    entityId,
    setShowCommentCard,
    showCommentCard,
    attachToRef,
}: CommentsPopOverProps): JSX.Element | null {
    const [newCommentText, setNewCommentText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [shouldScrollToLatest, setShouldScrollToLatest] = useState(false);
    const [showMentionPopover, setShowMentionPopover] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [triggerIndex, setTriggerIndex] = useState<number | null>(null);

    const { user } = useUserContext();
    const { members, getMember } = useMembers();
    const { t } = useTranslation();
    const performActionWithBlocks = usePerformActionWithYBlocks();
    const { id: spaceId } = useCurrentWorkspace();
    const [sendNotification] = api.useSendNotificationMutation();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const latestCommentRef = useRef<HTMLDivElement>(null);
    const commentsContainerRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const mentionListRef = useRef<HTMLDivElement>(null);

    const xOverflow = 400;
    const yOverflow = 100;

    const [popoverRef, contentRef] = usePopover({
        isOpen: showCommentCard,
        onClose: () => {
            setShowCommentCard(false);
        },
        target: useCallback(() => {
            if (attachToRef.current) {
                return attachToRef.current.getBoundingClientRect();
            }
            return null;
        }, [attachToRef]),
        clickOutsideRefs: [attachToRef],
        position: 'below',
        align: isMobile ? 'right' : 'left',
        overflow: {
            x: xOverflow,
            y: yOverflow,
        },
    });

    useEffect(() => {
        if (shouldScrollToLatest && comments && comments.length > 0 && latestCommentRef.current) {
            latestCommentRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setShouldScrollToLatest(false);
        }
    }, [shouldScrollToLatest, comments]);

    const filteredMembers = useMemo(() => {
        if (!mentionQuery) {
            return members;
        }
        const lowerCaseQuery = mentionQuery.toLowerCase();
        return members.filter((member) => {
            const fullName = getFullMemberName(member).toLowerCase();
            const firstName = member.firstName?.toLowerCase() ?? '';
            const lastName = member.lastName?.toLowerCase() ?? '';
            return (
                fullName.includes(lowerCaseQuery) ||
                firstName.includes(lowerCaseQuery) ||
                lastName.includes(lowerCaseQuery)
            );
        });
    }, [members, mentionQuery]);

    const handleMemberSelect = useCallback(
        (member: Member) => {
            const currentText = textareaRef.current?.value || '';
            const cursorPosition = textareaRef.current?.selectionStart || 0;

            if (triggerIndex !== null) {
                const textBefore = currentText.substring(0, triggerIndex);
                const textAfter = currentText.substring(cursorPosition);
                const mentionName = `@${getFullMemberName(member)} `;

                setNewCommentText(textBefore + mentionName + textAfter);

                const newCursorPosition = triggerIndex + mentionName.length;
                setTimeout(() => {
                    textareaRef.current?.focus();
                    textareaRef.current?.setSelectionRange(newCursorPosition, newCursorPosition);
                }, 0);
            }

            setShowMentionPopover(false);
            setMentionQuery('');
            setTriggerIndex(null);
        },
        [triggerIndex, setNewCommentText, setShowMentionPopover, setMentionQuery, setTriggerIndex],
    );

    const { index: mentionSelectedIndex, setIndex: setMentionSelectedIndex } = useArrowSelectableIndex({
        onSelect: (index) => {
            const member = filteredMembers[index];
            if (member) {
                handleMemberSelect(member);
            }
        },
        onUpdate(index) {
            if (mentionListRef.current) {
                scrollToSelectedIndex(mentionListRef.current);
            }
        },
        maxIndex: filteredMembers.length - 1,
        active: showMentionPopover,
        selectWithTab: true,
        initialIndex: 0,
    });

    useEffect(() => {
        if (showMentionPopover) {
            setMentionSelectedIndex(0);
        }
    }, [filteredMembers.length, showMentionPopover, setMentionSelectedIndex]);

    const getCommenterName = (userId: string | undefined | null): string => {
        if (!userId) return t('comments.annonymous_user');

        const member = getMember(userId);
        if (member) {
            if (member.firstName || member.lastName) {
                return `${member.firstName || ''} ${member.lastName || ''}`.trim();
            }
            if (userId === user?.id && user?.data.email) {
                return user.data.email;
            }
            return t('comments.member_user', { id: userId.substring(0, 6) });
        }

        return t('comments.unknown_user');
    };

    const getMentionedMemberIds = useCallback(
        (text: string): string[] => {
            const mentionedIds = new Set<string>();

            if (!text.includes('@')) {
                return [];
            }

            members.forEach((member) => {
                const fullName = getFullMemberName(member);
                if (!fullName) return;

                const searchPattern = `@${fullName}`;

                let startIndex = 0;
                let index = text.indexOf(searchPattern, startIndex);

                while (index !== -1) {
                    const precedingChar = index === 0 ? null : text[index - 1];
                    const isValidPrecedingChar = index === 0 || /\s/.test(precedingChar ?? '');

                    if (isValidPrecedingChar) {
                        const afterIndex = index + searchPattern.length;
                        const followingChar = afterIndex >= text.length ? null : text[afterIndex];
                        const isValidFollowingChar = followingChar === null || !/[\p{L}\p{M}0-9]/u.test(followingChar);

                        if (isValidFollowingChar) {
                            mentionedIds.add(member.id);
                        }
                    }

                    startIndex = index + 1;
                    index = text.indexOf(searchPattern, startIndex);
                }
            });

            const finalIds = Array.from(mentionedIds);
            return finalIds;
        },
        [members],
    );

    const triggerNotifications = useCallback(
        async (text: string) => {
            const mentionedIds = getMentionedMemberIds(text);

            for (const memberId of mentionedIds) {
                if (memberId === user?.id) continue;

                try {
                    const result = await sendNotification({
                        variables: {
                            input: {
                                pageId: entityId,
                                blockId: blockId,
                                memberId: memberId,
                                spaceId,
                            },
                        },
                    });
                    if (result) {
                        // console.log('Notification sent successfully:', result);
                    }
                } catch (error) {
                    console.error(`Failed to send notification to member ${memberId}`, error);
                    // Optionally show an error toast here
                }
            }
        },
        [user, sendNotification, entityId, blockId, spaceId, getMentionedMemberIds],
    );

    const submitComment = useCallback(async () => {
        if (isSubmitting) return;

        const trimmedText = newCommentText.trim();
        if (blockId && trimmedText) {
            setIsSubmitting(true);
            try {
                await triggerNotifications(trimmedText);
                performActionWithBlocks(SagaLocation.pageLocationFromId(entityId), (blocks) => {
                    SpaceOperations.addComment(blocks, blockId, trimmedText, user?.id);
                });
                setNewCommentText('');
                setShouldScrollToLatest(true);
            } finally {
                setIsSubmitting(false);
            }
        }
    }, [
        isSubmitting,
        blockId,
        newCommentText,
        triggerNotifications,
        performActionWithBlocks,
        entityId,
        user?.id,
        setNewCommentText,
        setShouldScrollToLatest,
    ]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (showMentionPopover) return;
        await submitComment();
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (showMentionPopover) {
            if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(e.key)) {
                if (e.key !== 'Escape') {
                    e.preventDefault();
                }
                if (e.key === 'Escape') {
                    setShowMentionPopover(false);
                }
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey && !showMentionPopover) {
            e.preventDefault();
            if (isSubmitting) return;
            await submitComment();
        }
    };

    const handleDelete = useCallback(
        (commentId: string) => {
            performActionWithBlocks(SagaLocation.pageLocationFromId(entityId), (blocks) => {
                SpaceOperations.deleteComment(blocks, blockId, commentId);
            });
        },
        [performActionWithBlocks, entityId, blockId],
    );

    const handleTextAreaChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const text = e.target.value;
            const cursorPosition = e.target.selectionStart;
            setNewCommentText(text);

            let query = '';
            let triggerFound = false;
            let currentTriggerIndex: number | null = null;

            for (let i = cursorPosition - 1; i >= 0; i--) {
                const char = text[i];
                if (char === '@' || char === '/') {
                    // Retained '/' for potential future use, but only '@' triggers member list
                    if (i === 0 || /\s/.test(text[i - 1])) {
                        query = text.substring(i + 1, cursorPosition);
                        if (!/\s/.test(query) && char === '@') {
                            // Only trigger for '@'
                            triggerFound = true;
                            currentTriggerIndex = i;
                            break;
                        } else {
                            break;
                        }
                    } else {
                        break;
                    }
                } else if (/\s/.test(char)) {
                    break;
                }
            }

            if (triggerFound && currentTriggerIndex !== null) {
                const potentialMembers = members.filter((member) => {
                    const lowerQuery = query.toLowerCase();
                    return getFullMemberName(member).toLowerCase().includes(lowerQuery);
                });

                if (query === '' || potentialMembers.length > 0) {
                    setMentionQuery(query);
                    setTriggerIndex(currentTriggerIndex);
                    setShowMentionPopover(true);
                    setMentionSelectedIndex(0);
                } else {
                    // Query is not empty and no potential members found, so hide popover
                    setShowMentionPopover(false);
                }
            } else {
                // If no active mention trigger, hide the popover and reset related state.
                setShowMentionPopover(false);
                setTriggerIndex(null);
                setMentionQuery('');
            }
        },
        [members, setNewCommentText, setMentionQuery, setTriggerIndex, setShowMentionPopover, setMentionSelectedIndex],
    );

    const handleMentionButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();

            if (textareaRef.current) {
                textareaRef.current.focus();

                const currentText = textareaRef.current.value;
                let cursorPosition = textareaRef.current.selectionStart;
                const charBeforeCursor = currentText[cursorPosition - 1];

                if (charBeforeCursor !== '@') {
                    const textBefore = currentText.substring(0, cursorPosition);
                    const textAfter = currentText.substring(cursorPosition);
                    const newText = textBefore + '@' + textAfter;

                    textareaRef.current.value = newText;
                    textareaRef.current.setSelectionRange(cursorPosition + 1, cursorPosition + 1);

                    setNewCommentText(newText);
                    setTriggerIndex(cursorPosition);

                    if (textareaRef.current) {
                        handleTextAreaChange({ target: textareaRef.current } as React.ChangeEvent<HTMLTextAreaElement>);
                    }
                } else {
                    textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
                    setTriggerIndex(cursorPosition - 1);

                    if (textareaRef.current) {
                        handleTextAreaChange({ target: textareaRef.current } as React.ChangeEvent<HTMLTextAreaElement>);
                    }
                }
            }
        },
        [setNewCommentText, setTriggerIndex, handleTextAreaChange],
    );

    return (
        <RadixPopOver
            isOpen={showCommentCard}
            ref={popoverRef}
            contentRef={contentRef}
            zIndex={400}
            onOpenAnimationDone={() => {
                textareaRef.current?.focus();
            }}
        >
            <div className="w-80 bg-white dark:bg-zinc-800 shadow-small rounded-md z-150" data-testid="comment-card">
                <div className="p-1 flex flex-col h-full max-h-[500px]">
                    {comments && comments.length > 0 && (
                        <div className="space-y-1.5 my-3 px-2 overflow-y-auto" ref={commentsContainerRef}>
                            {comments.map((comment, index) => {
                                const commentDate = new Date(comment.createdAt);
                                const now = new Date();
                                const isLastComment = index === comments.length - 1;
                                const commenterName = getCommenterName(comment.userId);
                                const commenterColor = comment.userId
                                    ? stringToColor(comment.userId, AVAILABLE_COLORS)
                                    : AVAILABLE_COLORS[0];

                                const isToday =
                                    commentDate.getDate() === now.getDate() &&
                                    commentDate.getMonth() === now.getMonth() &&
                                    commentDate.getFullYear() === now.getFullYear();
                                const isCurrentYear = commentDate.getFullYear() === now.getFullYear();

                                const timeFormatOptions: Intl.DateTimeFormatOptions = {
                                    hour: 'numeric',
                                    minute: 'numeric',
                                    hour12: true,
                                };
                                const dateFormatOptions: Intl.DateTimeFormatOptions = {
                                    month: 'numeric',
                                    day: 'numeric',
                                };
                                const yearFormatOptions: Intl.DateTimeFormatOptions = {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                };

                                const dateString = isToday
                                    ? commentDate.toLocaleTimeString(undefined, timeFormatOptions)
                                    : isCurrentYear
                                      ? `${commentDate.toLocaleDateString(undefined, dateFormatOptions)}, ${commentDate.toLocaleTimeString(undefined, timeFormatOptions)}`
                                      : `${commentDate.toLocaleDateString(undefined, yearFormatOptions)}, ${commentDate.toLocaleTimeString(undefined, timeFormatOptions)}`;

                                return (
                                    <div
                                        key={comment.id}
                                        ref={isLastComment ? latestCommentRef : null}
                                        className="text-sm group"
                                    >
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-1.5">
                                                    <MemberAvatar
                                                        name={commenterName}
                                                        color={commenterColor}
                                                        size="md"
                                                        showBorder={false}
                                                    />
                                                    <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        {commenterName}
                                                    </span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {dateString}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => handleDelete(comment.id)}
                                                    className="text-gray-400 hover:text-red-500 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                                                    aria-label="Delete comment"
                                                >
                                                    <Trash size={12} />
                                                </button>
                                            </div>
                                            <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap pb-1">
                                                {comment.text}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-auto sticky bottom-0 bg-white dark:bg-zinc-800 p-1">
                        <Popover.Root open={showMentionPopover} onOpenChange={setShowMentionPopover}>
                            <Popover.Anchor asChild>
                                <form ref={formRef} onSubmit={handleSubmit} className="flex items-end space-x-1">
                                    <textarea
                                        ref={textareaRef}
                                        className="flex-grow p-1 border-none rounded-md dark:bg-zinc-800 dark:text-white text-sm overflow-hidden resize-none focus:outline-none max-h-40"
                                        placeholder={t('comments.add_comment_placeholder')}
                                        value={newCommentText}
                                        onChange={handleTextAreaChange}
                                        onKeyDown={handleKeyDown}
                                        style={{ fieldSizing: 'content' } as React.CSSProperties}
                                        data-testid="comment-card-textarea"
                                        rows={1}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleMentionButtonClick}
                                        className={classNames(
                                            'rounded h-7 w-7 flex items-center justify-center flex-shrink-0',
                                            'cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700',
                                        )}
                                        data-testid="mention-button"
                                    >
                                        <span className="dark:text-gray-500 text-gray-400">@</span>
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!newCommentText.trim() || isSubmitting}
                                        className={classNames(
                                            'rounded h-7 w-7 flex items-center justify-center flex-shrink-0',
                                            {
                                                'cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-600 bg-white dark:bg-zinc-700 shadow-button':
                                                    newCommentText.trim() && !showMentionPopover && !isSubmitting,
                                                'opacity-50 dark:opacity-100 cursor-not-allowed':
                                                    !newCommentText.trim() || showMentionPopover || isSubmitting,
                                            },
                                        )}
                                        data-testid="add-comment-card-button"
                                    >
                                        <CornerDownLeft
                                            size={16}
                                            className={classNames('text-saga-gray-dark ', {
                                                'dark:stroke-white stroke-gray-700': newCommentText.trim(),
                                            })}
                                        />
                                    </button>
                                </form>
                            </Popover.Anchor>
                            <Popover.Portal>
                                <Popover.Content
                                    side="top"
                                    align="start"
                                    sideOffset={5}
                                    onOpenAutoFocus={(e) => e.preventDefault()}
                                    onCloseAutoFocus={(e) => e.preventDefault()}
                                    className="z-[500] w-64 bg-white dark:bg-zinc-800 shadow-lg rounded-md border border-gray-200 dark:border-zinc-700"
                                >
                                    <div ref={mentionListRef} className="max-h-48 overflow-y-auto p-1">
                                        {filteredMembers.map((member, index) => (
                                            <Button.PopOverButton
                                                key={member.id}
                                                selected={mentionSelectedIndex === index}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMemberSelect(member);
                                                }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                onMouseEnter={() => setMentionSelectedIndex(index)}
                                                data-selected={mentionSelectedIndex === index}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <MemberAvatar
                                                        name={getFullMemberName(member)}
                                                        color={stringToColor(member.id, AVAILABLE_COLORS)}
                                                        size="sm"
                                                        showBorder={false}
                                                    />
                                                    <span className="text-sm text-gray-900 dark:text-gray-100">
                                                        {getFullMemberName(member)}
                                                    </span>
                                                </div>
                                            </Button.PopOverButton>
                                        ))}
                                    </div>
                                </Popover.Content>
                            </Popover.Portal>
                        </Popover.Root>
                    </div>
                </div>
            </div>
        </RadixPopOver>
    );
}
